import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { TranslateModule } from '@ngx-translate/core';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { AuthService } from 'src/app/Auth/AuthService';

@Component({
  selector: 'app-add-card',
  standalone: true,
  imports: [
    NzSelectModule,
    FormsModule,
    CommonModule,
    RouterLink,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './add-card.component.html',
  styleUrl: './add-card.component.scss',
})
export class AddCardComponent {
  billingForm: any;
  constructor(
    private router: Router,
    private fb: FormBuilder,
    private pagesService: PagesService,
    private authService: AuthService,
    private sharedService: SharedService,
    private route: ActivatedRoute
  ) { }
  isEditMode: boolean = false;
  subscriptionId: string | null = null;
  selectedCountryZipPattern: string = '';
  selectedCountryMaxLength: number = 6;
  selectedValue = null;
  selectedPayment = 'credit-card';
  selectedSubscription = 'monthly';
  /** Always true: this screen is switched off, see the note inside. */
  private redirectUnavailable(): boolean {
    // Saving a card is switched off on the gateway: it has no Stripe customer /
    // SetupIntent flow and the form here collects a raw card number + CVV, which
    // must never reach (or be stored by) our servers. Plan payments go through
    // Stripe Checkout on the subscription page instead.
    this.sharedService.createNotification(
      'info',
      'Saving cards is not available. Payments are handled securely at checkout.'
    );
    this.router.navigate(['home/billing']);
    return true;
  }

  ngOnInit() {
    // Build the form first so the template never binds to an undefined group while the redirect runs.
    this.initializeForm();
    if (this.redirectUnavailable()) return;
    this.billingForm.get('user_id').setValue(this.authService.getUserId());
    this.billingForm.get('email').setValue(this.authService.getUserEmail());

    this.subscriptionId = this.route.snapshot.queryParamMap.get('id');
    this.isEditMode = !!this.subscriptionId;

    if (this.isEditMode && this.subscriptionId) {
      this.getCardDetails(this.subscriptionId);
    }

    this.billingForm.get('country')?.valueChanges.subscribe((selectedCountry: string) => {
      this.onCountryChange(selectedCountry);
    });
  }

  // Countries with zip patterns
  countries = [
    { name: "Afghanistan", zipPattern: "4 digits", example: "1001" },
    { name: "Albania", zipPattern: "4 digits", example: "1001" },
    { name: "Algeria", zipPattern: "5 digits", example: "16000" },
    { name: "Andorra", zipPattern: "5 characters", example: "AD100" },
    { name: "Angola", zipPattern: "No postal code", example: "" },
    { name: "Antigua and Barbuda", zipPattern: "No postal code", example: "" },
    { name: "Argentina", zipPattern: "4 or 8 characters", example: "C1000AAA" },
    { name: "Armenia", zipPattern: "4 digits", example: "0010" },
    { name: "Australia", zipPattern: "4 digits", example: "2000" },
    { name: "Austria", zipPattern: "4 digits", example: "1010" },
    { name: "Azerbaijan", zipPattern: "4 digits", example: "1000" },
    { name: "Bahamas", zipPattern: "No postal code", example: "" },
    { name: "Bahrain", zipPattern: "3 or 4 digits", example: "317" },
    { name: "Bangladesh", zipPattern: "4 digits", example: "1000" },
    { name: "Barbados", zipPattern: "5 characters", example: "BB100" },
    { name: "Belarus", zipPattern: "6 digits", example: "220050" },
    { name: "Belgium", zipPattern: "4 digits", example: "1000" },
    { name: "Belize", zipPattern: "No postal code", example: "" },
    { name: "Benin", zipPattern: "No postal code", example: "" },
    { name: "Bhutan", zipPattern: "5 digits", example: "11001" },
    { name: "Bolivia", zipPattern: "4 digits", example: "0001" },
    { name: "Bosnia and Herzegovina", zipPattern: "5 digits", example: "71000" },
    { name: "Botswana", zipPattern: "No postal code", example: "" },
    { name: "Brazil", zipPattern: "8 digits", example: "01310-100" },
    { name: "Brunei", zipPattern: "6 characters", example: "BA1411" },
    { name: "Bulgaria", zipPattern: "4 digits", example: "1000" },
    { name: "Burkina Faso", zipPattern: "No postal code", example: "" },
    { name: "Burundi", zipPattern: "No postal code", example: "" },
    { name: "Cambodia", zipPattern: "5 digits", example: "120101" },
    { name: "Cameroon", zipPattern: "No postal code", example: "" },
    { name: "Canada", zipPattern: "6 characters", example: "K1A 0A6" },
    { name: "Cape Verde", zipPattern: "4 digits", example: "7600" },
    { name: "Central African Republic", zipPattern: "No postal code", example: "" },
    { name: "Chad", zipPattern: "No postal code", example: "" },
    { name: "Chile", zipPattern: "7 digits", example: "8320000" },
    { name: "China", zipPattern: "6 digits", example: "100000" },
    { name: "Colombia", zipPattern: "6 digits", example: "110221" },
    { name: "Comoros", zipPattern: "No postal code", example: "" },
    { name: "Congo", zipPattern: "No postal code", example: "" },
    { name: "Costa Rica", zipPattern: "5 digits", example: "10101" },
    { name: "Croatia", zipPattern: "5 digits", example: "10000" },
    { name: "Cuba", zipPattern: "5 digits", example: "10100" },
    { name: "Cyprus", zipPattern: "4 digits", example: "1010" },
    { name: "Czech Republic", zipPattern: "5 digits", example: "110 00" },
    { name: "Denmark", zipPattern: "4 digits", example: "1000" },
    { name: "Djibouti", zipPattern: "No postal code", example: "" },
    { name: "Dominica", zipPattern: "No postal code", example: "" },
    { name: "Dominican Republic", zipPattern: "5 digits", example: "10101" },
    { name: "Ecuador", zipPattern: "6 digits", example: "170515" },
    { name: "Egypt", zipPattern: "5 digits", example: "11511" },
    { name: "El Salvador", zipPattern: "4 digits", example: "1101" },
    { name: "Equatorial Guinea", zipPattern: "No postal code", example: "" },
    { name: "Eritrea", zipPattern: "No postal code", example: "" },
    { name: "Estonia", zipPattern: "5 digits", example: "10101" },
    { name: "Eswatini", zipPattern: "4 characters", example: "H100" },
    { name: "Ethiopia", zipPattern: "4 digits", example: "1000" },
    { name: "Fiji", zipPattern: "No postal code", example: "" },
    { name: "Finland", zipPattern: "5 digits", example: "00100" },
    { name: "France", zipPattern: "5 digits", example: "75001" },
    { name: "Gabon", zipPattern: "No postal code", example: "" },
    { name: "Gambia", zipPattern: "No postal code", example: "" },
    { name: "Georgia", zipPattern: "4 digits", example: "0101" },
    { name: "Germany", zipPattern: "5 digits", example: "10115" },
    { name: "Ghana", zipPattern: "No postal code", example: "" },
    { name: "Greece", zipPattern: "5 digits", example: "10431" },
    { name: "Grenada", zipPattern: "No postal code", example: "" },
    { name: "Guatemala", zipPattern: "5 digits", example: "01001" },
    { name: "Guinea", zipPattern: "3 digits", example: "001" },
    { name: "Guinea-Bissau", zipPattern: "4 digits", example: "1000" },
    { name: "Guyana", zipPattern: "No postal code", example: "" },
    { name: "Haiti", zipPattern: "4 digits", example: "1110" },
    { name: "Honduras", zipPattern: "5 digits", example: "11101" },
    { name: "Hungary", zipPattern: "4 digits", example: "1011" },
    { name: "Iceland", zipPattern: "3 digits", example: "101" },
    { name: "India", zipPattern: "6 digits", example: "110001" },
    { name: "Indonesia", zipPattern: "5 digits", example: "10110" },
    { name: "Iran", zipPattern: "10 digits", example: "1234567890" },
    { name: "Iraq", zipPattern: "5 digits", example: "10001" },
    { name: "Ireland", zipPattern: "7 characters", example: "D01 F5P2" },
    { name: "Israel", zipPattern: "7 digits", example: "9100001" },
    { name: "Italy", zipPattern: "5 digits", example: "00118" },
    { name: "Ivory Coast", zipPattern: "No postal code", example: "" },
    { name: "Jamaica", zipPattern: "2 digits", example: "01" },
    { name: "Japan", zipPattern: "7 digits", example: "100-0001" },
    { name: "Jordan", zipPattern: "5 digits", example: "11181" },
    { name: "Kazakhstan", zipPattern: "6 digits", example: "010000" },
    { name: "Kenya", zipPattern: "5 digits", example: "00100" },
    { name: "Kiribati", zipPattern: "No postal code", example: "" },
    { name: "Kuwait", zipPattern: "5 digits", example: "13001" },
    { name: "Kyrgyzstan", zipPattern: "6 digits", example: "720001" },
    { name: "Laos", zipPattern: "5 digits", example: "01000" },
    { name: "Latvia", zipPattern: "4 digits", example: "1010" },
    { name: "Lebanon", zipPattern: "4 digits", example: "1107" },
    { name: "Lesotho", zipPattern: "3 digits", example: "100" },
    { name: "Liberia", zipPattern: "4 digits", example: "1000" },
    { name: "Libya", zipPattern: "5 digits", example: "21335" },
    { name: "Liechtenstein", zipPattern: "4 digits", example: "9490" },
    { name: "Lithuania", zipPattern: "5 digits", example: "01113" },
    { name: "Luxembourg", zipPattern: "4 digits", example: "1009" },
    { name: "Madagascar", zipPattern: "3 digits", example: "101" },
    { name: "Malawi", zipPattern: "No postal code", example: "" },
    { name: "Malaysia", zipPattern: "5 digits", example: "50000" },
    { name: "Maldives", zipPattern: "5 digits", example: "20026" },
    { name: "Mali", zipPattern: "No postal code", example: "" },
    { name: "Malta", zipPattern: "7 characters", example: "VLT 1117" },
    { name: "Marshall Islands", zipPattern: "5 digits", example: "96960" },
    { name: "Mauritania", zipPattern: "No postal code", example: "" },
    { name: "Mauritius", zipPattern: "5 digits", example: "11001" },
    { name: "Mexico", zipPattern: "5 digits", example: "01000" },
    { name: "Micronesia", zipPattern: "5 digits", example: "96941" },
    { name: "Moldova", zipPattern: "4 digits", example: "2001" },
    { name: "Monaco", zipPattern: "5 digits", example: "98000" },
    { name: "Mongolia", zipPattern: "5 digits", example: "14200" },
    { name: "Montenegro", zipPattern: "5 digits", example: "81000" },
    { name: "Morocco", zipPattern: "5 digits", example: "10000" },
    { name: "Mozambique", zipPattern: "4 digits", example: "1100" },
    { name: "Myanmar", zipPattern: "5 digits", example: "11011" },
    { name: "Namibia", zipPattern: "5 digits", example: "10001" },
    { name: "Nauru", zipPattern: "No postal code", example: "" },
    { name: "Nepal", zipPattern: "5 digits", example: "44600" },
    { name: "Netherlands", zipPattern: "6 characters", example: "1012 AB" },
    { name: "New Zealand", zipPattern: "4 digits", example: "1010" },
    { name: "Nicaragua", zipPattern: "5 digits", example: "11001" },
    { name: "Niger", zipPattern: "4 digits", example: "8001" },
    { name: "Nigeria", zipPattern: "6 digits", example: "100001" },
    { name: "North Korea", zipPattern: "6 digits", example: "999093" },
    { name: "North Macedonia", zipPattern: "4 digits", example: "1000" },
    { name: "Norway", zipPattern: "4 digits", example: "0010" },
    { name: "Oman", zipPattern: "3 digits", example: "100" },
    { name: "Pakistan", zipPattern: "5 digits", example: "54000" },
    { name: "Palau", zipPattern: "5 digits", example: "96940" },
    { name: "Palestine", zipPattern: "No postal code", example: "" },
    { name: "Panama", zipPattern: "4 digits", example: "0801" },
    { name: "Papua New Guinea", zipPattern: "3 digits", example: "111" },
    { name: "Paraguay", zipPattern: "4 digits", example: "1536" },
    { name: "Peru", zipPattern: "5 digits", example: "15001" },
    { name: "Philippines", zipPattern: "4 digits", example: "1000" },
    { name: "Poland", zipPattern: "5 digits", example: "00-950" },
    { name: "Portugal", zipPattern: "7 digits", example: "1000-001" },
    { name: "Qatar", zipPattern: "No postal code", example: "" },
    { name: "Romania", zipPattern: "6 digits", example: "010041" },
    { name: "Russia", zipPattern: "6 digits", example: "101000" },
    { name: "Rwanda", zipPattern: "No postal code", example: "" },
    { name: "Saint Kitts and Nevis", zipPattern: "No postal code", example: "" },
    { name: "Saint Lucia", zipPattern: "No postal code", example: "" },
    { name: "Saint Vincent and the Grenadines", zipPattern: "4 digits", example: "VC0100" },
    { name: "Samoa", zipPattern: "No postal code", example: "" },
    { name: "San Marino", zipPattern: "5 digits", example: "47890" },
    { name: "Sao Tome and Principe", zipPattern: "No postal code", example: "" },
    { name: "Saudi Arabia", zipPattern: "5 digits", example: "11564" },
    { name: "Senegal", zipPattern: "5 digits", example: "12500" },
    { name: "Serbia", zipPattern: "5 digits", example: "11000" },
    { name: "Seychelles", zipPattern: "No postal code", example: "" },
    { name: "Sierra Leone", zipPattern: "No postal code", example: "" },
    { name: "Singapore", zipPattern: "6 digits", example: "018956" },
    { name: "Slovakia", zipPattern: "5 digits", example: "81101" },
    { name: "Slovenia", zipPattern: "4 digits", example: "1000" },
    { name: "Solomon Islands", zipPattern: "No postal code", example: "" },
    { name: "Somalia", zipPattern: "No postal code", example: "" },
    { name: "South Africa", zipPattern: "4 digits", example: "0001" },
    { name: "South Korea", zipPattern: "5 digits", example: "03049" },
    { name: "South Sudan", zipPattern: "No postal code", example: "" },
    { name: "Spain", zipPattern: "5 digits", example: "28001" },
    { name: "Sri Lanka", zipPattern: "5 digits", example: "00100" },
    { name: "Sudan", zipPattern: "5 digits", example: "11111" },
    { name: "Suriname", zipPattern: "No postal code", example: "" },
    { name: "Sweden", zipPattern: "5 digits", example: "11120" },
    { name: "Switzerland", zipPattern: "4 digits", example: "1000" },
    { name: "Syria", zipPattern: "No postal code", example: "" },
    { name: "Taiwan", zipPattern: "5 digits", example: "10041" },
    { name: "Tajikistan", zipPattern: "6 digits", example: "734025" },
    { name: "Tanzania", zipPattern: "No postal code", example: "" },
    { name: "Thailand", zipPattern: "5 digits", example: "10100" },
    { name: "Togo", zipPattern: "No postal code", example: "" },
    { name: "Tonga", zipPattern: "No postal code", example: "" },
    { name: "Trinidad and Tobago", zipPattern: "6 digits", example: "110123" },
    { name: "Tunisia", zipPattern: "4 digits", example: "1000" },
    { name: "Turkey", zipPattern: "5 digits", example: "34000" },
    { name: "Turkmenistan", zipPattern: "6 digits", example: "744000" },
    { name: "Tuvalu", zipPattern: "No postal code", example: "" },
    { name: "Uganda", zipPattern: "No postal code", example: "" },
    { name: "Ukraine", zipPattern: "5 digits", example: "01001" },
    { name: "United Arab Emirates", zipPattern: "No postal code", example: "" },
    { name: "United Kingdom", zipPattern: "6-8 characters", example: "SW1A 1AA" },
    { name: "United States", zipPattern: "5 or 9 digits", example: "10001" },
    { name: "Uruguay", zipPattern: "5 digits", example: "11000" },
    { name: "Uzbekistan", zipPattern: "6 digits", example: "100029" },
    { name: "Vanuatu", zipPattern: "No postal code", example: "" },
    { name: "Vatican City", zipPattern: "5 digits", example: "00120" },
    { name: "Venezuela", zipPattern: "4 digits", example: "1010" },
    { name: "Vietnam", zipPattern: "6 digits", example: "100000" },
    { name: "Yemen", zipPattern: "No postal code", example: "" },
    { name: "Zambia", zipPattern: "5 digits", example: "10101" },
    { name: "Zimbabwe", zipPattern: "No postal code", example: "" }
  ];
 onCountryChange(selectedCountry: string) {
    const country = this.countries.find(c => c.name === selectedCountry);
    if (country) {
      this.selectedCountryZipPattern = country.example ? 
        `Example: ${country.example} (${country.zipPattern})` : 
        country.zipPattern;
      
      this.updateZipValidation(country);
      this.updateZipMaxLength(country);
      this.billingForm.get('zip_code')?.setValue('');
    }
  }
  updateZipMaxLength(country: any) {
    let maxLength = 10; 
    
    if (country.zipPattern === "No postal code") {
      maxLength = 0;
    } else if (country.zipPattern.includes("3 digits")) {
      maxLength = 3;
    } else if (country.zipPattern.includes("4 digits")) {
      maxLength = 4;
    } else if (country.zipPattern.includes("5 digits")) {
      maxLength = 5;
    } else if (country.zipPattern.includes("6 digits")) {
      maxLength = 6;
    } else if (country.zipPattern.includes("7")) {
      maxLength = 7;
    } else if (country.zipPattern.includes("8")) {
      maxLength = 8;
    } else if (country.zipPattern.includes("10")) {
      maxLength = 10;
    }
    this.selectedCountryMaxLength = maxLength;
  }

  updateZipValidation(country: any) {
    const zipControl = this.billingForm.get('zip_code');
    
    if (country.zipPattern === "No postal code") {
      zipControl?.clearValidators();
      zipControl?.setValidators([]);
    } else {
      let pattern: RegExp;
      
      switch (country.zipPattern) {
        case "3 digits":
          pattern = /^\d{3}$/;
          break;
        case "4 digits":
          pattern = /^\d{4}$/;
          break;
        case "5 digits":
          pattern = /^\d{5}$/;
          break;
        case "6 digits":
          pattern = /^\d{6}$/;
          break;
        case "7 digits":
          pattern = /^\d{7}$/;
          break;
        case "8 digits":
          pattern = /^\d{8}-?\d{3}$/;
          break;
        case "10 digits":
          pattern = /^\d{10}$/;
          break;
        case "6 characters":
          pattern = /^[A-Z0-9]{6}$/i;
          break;
        case "7 characters":
          pattern = /^[A-Z0-9\s-]{7}$/i;
          break;
        case "5 characters":
          pattern = /^[A-Z0-9]{5}$/i;
          break;
        case "4 or 8 characters":
          pattern = /^([A-Z0-9]{4}|[A-Z0-9]{8})$/i;
          break;
        case "5 or 9 digits":
          pattern = /^(\d{5}|\d{9})$/;
          break;
        case "6-8 characters":
          pattern = /^[A-Z0-9\s]{6,8}$/i;
          break;
        case "2 digits":
          pattern = /^\d{2}$/;
          break;
        case "4 characters":
          pattern = /^[A-Z0-9]{4}$/i;
          break;
        case "3 or 4 digits":
          pattern = /^(\d{3}|\d{4})$/;
          break;
        default:
          pattern = /^[A-Z0-9\s-]{1,10}$/i;
      }
      
      zipControl?.setValidators([Validators.required, Validators.pattern(pattern)]);
    }
    
    zipControl?.updateValueAndValidity();
  }

  initializeForm() {
    this.billingForm = this.fb.group({
      user_id: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      payment_method: ['credit-card', Validators.required],
      card_number: [
        '',
        [Validators.required, Validators.pattern(/^\d{4} \d{4} \d{4} \d{4}$/)],
      ],
      expiry: [
        '',
        [Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])\/\d{2}$/)],
      ],
      cvv: ['', [Validators.required, Validators.pattern(/^\d{3}$/)]],
      country: ['', Validators.required],
      zip_code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      set_primary: [false],
    });
  }

  selectPaymentMethod(method: string) {
    this.selectedPayment = method;
    this.billingForm.patchValue({ payment_method: method });
  }

  selectSubscription(subscription: string) {
    this.selectedSubscription = subscription;
  }

  formatExpiry(event: any) {
    let input = event.target.value;
    const isBackspace = event.inputType === 'deleteContentBackward'; // Check if backspace was pressed

    input = input.replace(/\D/g, '');

    if (input.length >= 2) {
      const month = input.substring(0, 2);
      const year = input.substring(2);
      input = isBackspace && input.length <= 2 ? month : `${month}/${year}`;
    }
    event.target.value = input;
    this.billingForm.controls['expiry'].setValue(input);
  }

  formatCvv(event: any) {
    let input = event.target.value;

    input = input.replace(/\D/g, '');
    if (input.length > 3) {
      input = input.substring(0, 3);
    }
    event.target.value = input;
    this.billingForm.controls['cvv'].setValue(input);
  }

  formatBankNumber(event: any) {
    let input = event.target.value;
    input = input.replace(/\D/g, '');
    input = input.replace(/(\d{4})(?=\d)/g, '$1 ');
    if (input.length > 19) {
      input = input.substring(0, 19);
    }

    event.target.value = input.trim();
    this.billingForm.controls['card_number'].setValue(input);
  }

  submitForm() {
    this.sharedService.showLoader();

    if (this.billingForm.valid) {
      const expiryValue = this.billingForm.get('expiry')?.value;
      if (expiryValue) {
        const [month, year] = expiryValue.split('/');
        const expiry = new Date(2000 + parseInt(year), parseInt(month) - 1);
        const today = new Date();

        if (expiry < today) {
          this.sharedService.createNotification('error', 'Card has expired');
          this.sharedService.hideLoader();
          return;
        }
      }

      if (this.isEditMode) {
        this.updateCard();
        this.sharedService.hideLoader();
      } else {
        this.authService.login('BANK_CARD', this.billingForm.value).subscribe({
          next: (response: any) => {
            this.sharedService.createNotification(
              'success',
              'Card added Successfully'
            );
            this.sharedService.hideLoader();
            this.router.navigate(['home/billing']);
          },
        });
      }
    } else {
      const invalidFields: string[] = [];

      Object.keys(this.billingForm.controls).forEach((key) => {
        const control = this.billingForm.get(key);
        if (control && control.invalid) {
          invalidFields.push(key);
          control.markAsTouched();
        }
      });

      const errorMessage = `Please correct the following fields: ${invalidFields.join(
        ', '
      )}`;
      this.sharedService.createNotification('error', errorMessage);
      this.sharedService.hideLoader();
    }
  }

  backToBilling() {
    this.router.navigate(['/home/billing']);
  }

  updateCard() {
    this.sharedService.showLoader();
    const data = {
      card_id: this.subscriptionId,
      user_id: this.authService.getUserId(),
      payment_method: this.billingForm.get('payment_method').value,
      card_number: this.billingForm.get('card_number').value,
      expiry: this.billingForm.get('expiry').value,
      cvv: this.billingForm.get('cvv').value,
      country: this.billingForm.get('country').value,
      zip_code: this.billingForm.get('zip_code').value,
      set_primary: this.billingForm.get('set_primary').value,
      email: this.billingForm.get('email').value,
    };
    this.pagesService
      .updateCardDetail('BANK_CARD', this.subscriptionId, data)
      .subscribe({
        next: (response: any) => {
          if (response && response.card_number) {
            this.sharedService.createNotification(
              'success',
              'Card updated Successfully'
            );
            this.backToBilling();
          }
          this.sharedService.hideLoader();
        },
        error: (error) => {
          this.sharedService.createNotification('error', error?.error?.message || error);
          this.sharedService.hideLoader();
        },
      });
  }

  getCardDetails(id: string) {
    this.sharedService.showLoader();
    this.pagesService
      .getCardDetail('BANK_CARD', this.subscriptionId)
      .subscribe({
        next: (response: any) => {
          if (response && response.card_number) {
            this.billingForm.patchValue({
              card_number: response.card_number,
              email: response.email,
              expiry: response.expiry,
              cvv: response.cvv,
              country: response.country,
              zip_code: response.zip_code,
              set_primary: response.set_primary || false,
            });
            this.selectedPayment = response.payment_method || 'credit-card';
          }
          this.sharedService.hideLoader();
        },
        error: (error) => {
          this.sharedService.hideLoader();
          this.sharedService.createNotification('error', error?.error?.message || error);
        },
      });
  }

}
